import toast from 'react-hot-toast'

import { showApiError, useMutation, useQueryClient } from '@fv/client-core'
import type { ChangeUserCarrierDTO } from '@fv/models'

import { usersQueryKeys } from '@/hooks/useFetchUsers'
import { adminFetch } from '@/utils'

async function changeCarrier({
  carrierId,
  userId,
}: ChangeUserCarrierDTO & { userId: string }) {
  return adminFetch(`/users/${userId}/carrier`, {
    method: 'PUT',
    body: { carrierId },
  })
}

export function useChangeCarrier(userId: string) {
  return useMutation(
    (carrierId: string) => changeCarrier({ userId, carrierId }),
    {
      onError: e => {
        showApiError('Error changing carrier', e)
      },
      onSuccess: () => {
        toast.success('Carrier changed')
      },
    },
  )
}

async function verifyUser(userId: string) {
  return adminFetch(`/users/${userId}/verify`, {
    method: 'POST',
  })
}

export function useVerifyUser() {
  const queryClient = useQueryClient()
  return useMutation(verifyUser, {
    onError: e => {
      showApiError('Error verifying user', e)
    },
    onSuccess: () => {
      toast.success('User verified')
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.all })
    },
  })
}

async function unlockUser(userId: string) {
  return adminFetch(`/users/${userId}/unlock`, {
    method: 'POST',
  })
}

export function useUnlockUser() {
  const queryClient = useQueryClient()
  return useMutation(unlockUser, {
    onError: e => {
      showApiError('Error unlocking user', e)
    },
    onSuccess: () => {
      toast.success('User unlocked')
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.all })
    },
  })
}

async function removeUser(userId: string) {
  return adminFetch(`/users/${userId}`, {
    method: 'DELETE',
  })
}

export function useRemoveUser() {
  const queryClient = useQueryClient()
  return useMutation(removeUser, {
    onError: e => showApiError('Error removing user', e),
    onSuccess: () => {
      toast.success('User removed')
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.all })
    },
  })
}
